import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Gift, Trash2, Check, X, Mail, Phone, Building2, Calendar, ExternalLink } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "pending",         label: "Eingegangen",         color: "bg-amber-100 text-amber-800" },
  { value: "contacted",       label: "Kontaktiert",         color: "bg-sky-100 text-sky-800" },
  { value: "contract_signed", label: "Vertrag unterschrieben", color: "bg-emerald-100 text-emerald-800" },
  { value: "credited",        label: "Gutschrift gebucht",  color: "bg-purple-100 text-purple-800" },
  { value: "rejected",        label: "Abgelehnt",           color: "bg-rose-100 text-rose-800" },
];

export default function AdminReferrals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/referrals");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/referrals/${id}`, { status });
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm("Empfehlung wirklich löschen?")) return;
    await api.delete(`/admin/referrals/${id}`);
    await load();
  };

  // Stats
  const year = new Date().getFullYear();
  const thisYear = items.filter((i) => i.year === year);
  const signed = thisYear.filter((i) => i.status === "contract_signed" || i.status === "credited").length;
  const pending = thisYear.filter((i) => i.status === "pending").length;

  return (
    <div data-testid="admin-referrals">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1A2433] tracking-tight">Empfehlungen</h1>
          <p className="text-sm text-slate-500">Mandanten werben Mandanten · CHF 500 Gutschrift bei unterschriebenem Vertrag.</p>
        </div>
        <button onClick={load} className="text-sm text-[#991B1B] hover:underline">Aktualisieren</button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total" value={items.length} icon={Gift} />
        <StatCard label={`Eingegangen ${year}`} value={pending} icon={Calendar} />
        <StatCard label={`Verträge ${year}`} value={signed} icon={Check} />
        <StatCard label={`Auszahlung CHF ${year}`} value={signed * 500} icon={Gift} suffix="" />
      </div>

      {loading ? (
        <div className="text-slate-500">Lade…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">
          Noch keine Empfehlungen eingegangen.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => {
            const meta = STATUS_OPTIONS.find((s) => s.value === r.status) || STATUS_OPTIONS[0];
            return (
              <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`admin-referral-${r.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">
                      {new Date(r.created_at).toLocaleString("de-CH")} · {r.year}
                    </div>
                    <div className="font-heading font-bold text-[#1A2433]">
                      {r.referrer_name} → {r.referee_name}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${meta.color}`}>
                      {meta.label}
                    </span>
                    <button
                      onClick={() => remove(r.id)}
                      className="p-1.5 text-slate-400 hover:text-rose-600"
                      data-testid={`admin-referral-delete-${r.id}`}
                      title="Löschen"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <Block title="Werber:in" name={r.referrer_name} email={r.referrer_email} phone={r.referrer_phone} />
                  <Block title="Empfohlene Person" name={r.referee_name} email={r.referee_email} phone={r.referee_phone} company={r.referee_company} />
                </div>

                {r.note && (
                  <div className="mt-3 text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
                    <strong className="text-[#1A2433]">Notiz:</strong> {r.note}
                  </div>
                )}

                <div className="mt-4 flex flex-wrap gap-2 items-center">
                  <span className="text-xs text-slate-500 uppercase font-semibold">Status setzen:</span>
                  {STATUS_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => updateStatus(r.id, opt.value)}
                      disabled={r.status === opt.value}
                      className={`text-xs px-2.5 py-1 rounded-full border transition ${
                        r.status === opt.value
                          ? "bg-[#1A2433] text-white border-[#1A2433] cursor-default"
                          : "bg-white border-slate-200 text-slate-700 hover:bg-slate-100"
                      }`}
                      data-testid={`admin-referral-status-${r.id}-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {r.status === "contract_signed" && (
                  <div className="mt-3 bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-lg p-3 flex items-start gap-2">
                    <Gift className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Action erforderlich:</strong> CHF 500 Gutschrift im AbaNinja auf das nächste Honorar von
                      <strong> {r.referrer_name}</strong> ({r.referrer_email}) buchen, danach Status auf „Gutschrift gebucht" setzen.
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, suffix }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-start justify-between mb-2">
        <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
        <Icon className="w-4 h-4 text-[#991B1B]" />
      </div>
      <div className="font-heading text-2xl font-bold text-[#1A2433]">
        {typeof value === "number" && value > 999 ? value.toLocaleString("de-CH") : value}
        {suffix !== undefined ? "" : ""}
      </div>
    </div>
  );
}

function Block({ title, name, email, phone, company }) {
  return (
    <div className="bg-slate-50 rounded-lg p-3">
      <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1.5">{title}</div>
      <div className="font-semibold text-[#1A2433] mb-1">{name}</div>
      <div className="text-slate-600 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> <a href={`mailto:${email}`} className="text-[#991B1B] hover:underline">{email}</a></div>
      {phone && <div className="text-slate-600 flex items-center gap-1.5 mt-0.5"><Phone className="w-3.5 h-3.5" /> {phone}</div>}
      {company && <div className="text-slate-600 flex items-center gap-1.5 mt-0.5"><Building2 className="w-3.5 h-3.5" /> {company}</div>}
    </div>
  );
}
